import React, {useEffect, useMemo} from "react";
import {Box, NumberInput, SegmentedControl, Text} from "@mantine/core";
import {DeepPartial} from "@/utils/types/DeepPartial";
import {TWorkflow} from "../StepView";
import {
    IMeasurePLRStep,
    IPupillometryReading,
} from "@/protocols/types/protocol";
import {IMeasurePLRStepDefinition} from "@/protocols/types/protocolDefinitions";
import {ZoneAwareDateTimePicker} from "@/common/components/ZoneAwareDateTimePicker";
import {NaiveDateTimePicker} from "@/common/components/NaiveDateTimePicker";
import dayjs from "dayjs";
import classes from "./MeasurePlrStepView.module.css";

type MeasurePlrStep = IMeasurePLRStep | IMeasurePLRStepDefinition;

const DEVICE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss";
const DEVICE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

export interface MeasurePlrStepViewProps {
    step: MeasurePlrStep;
    timezone?: string;
    onChange: (updates: DeepPartial<MeasurePlrStep>) => void;
    workflow?: TWorkflow;
}

const buildDefaultReadings = (count: number): IPupillometryReading[] => {
    return Array.from({length: count}, (_, index) => ({
        is_left: index % 2 === 0,
        captured_at: undefined,
        device_time: undefined,
    }));
};

const normalizeReadings = (
    count: number,
    readings: IPupillometryReading[] | undefined,
): IPupillometryReading[] => {
    const current = readings || [];
    const next = current.slice(0, count).map((reading, index) => ({
        is_left: reading?.is_left ?? index % 2 === 0,
        captured_at: reading?.captured_at,
        device_time: reading?.device_time,
    }));

    if (next.length < count) {
        next.push(...buildDefaultReadings(count - next.length).map((reading, idx) => ({
            ...reading,
            is_left: (next.length + idx) % 2 === 0,
        })));
    }

    return next;
};

const parseNaiveDeviceTime = (value?: string) => {
    if (!value || !DEVICE_TIME_REGEX.test(value)) {
        return undefined;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : undefined;
};

export const MeasurePlrStepView: React.FC<MeasurePlrStepViewProps> = ({
    step,
    timezone,
    onChange,
    workflow = "session",
}) => {
    const isPlanningMode = workflow === "planning";
    const isReadOnly = workflow === "review";
    const canEditTimes = workflow === "session" || workflow === "review_edit";
    const canEditSide = isPlanningMode && !isReadOnly;

    const count = step.count || 2;
    const readings = step.readings || [];

    const rows = useMemo(() => normalizeReadings(count, readings), [count, readings]);

    useEffect(() => {
        if (rows.length !== readings.length) {
            onChange({readings: rows});
        }
    }, [rows, readings.length, onChange]);

    const onCountBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!isPlanningMode || isReadOnly) {
            return;
        }
        if (e.currentTarget.value === "") {
            e.currentTarget.value = count.toString();
        } else {
            const nextCount = parseInt(e.currentTarget.value, 10);
            if (isNaN(nextCount) || nextCount <= 0) {
                return;
            }
            onChange({
                count: nextCount,
                readings: normalizeReadings(nextCount, readings),
            });
        }
    };

    const updateReading = (index: number, updates: Partial<IPupillometryReading>) => {
        const next = [...rows];
        next[index] = {
            ...next[index],
            ...updates,
        };
        onChange({readings: next});
    };

    return (
        <Box className={classes.container}>
            {isPlanningMode && (
                <Box className={classes.countInput}>
                    <NumberInput
                        label="Number of Readings"
                        value={count}
                        min={1}
                        max={20}
                        clampBehavior="strict"
                        hideControls
                        disabled={!isPlanningMode || isReadOnly}
                        onBlur={onCountBlur}
                    />
                </Box>
            )}

            <Box className={classes.readingsList}>
                {rows.map((reading, index) => (
                    <Box
                        key={`plr-reading-${index}`}
                        className={classes.readingRow}
                    >

                        <Box className={classes.readingFields}>
                            <Box className={classes.sideSelector}>
                                <Text size="sm" fw={500} mb="0.25rem">Reading {index + 1}</Text>
                                <SegmentedControl
                                    fullWidth
                                    data={[
                                        {label: "Left Eye", value: "left"},
                                        {label: "Right Eye", value: "right"},
                                    ]}
                                    value={reading.is_left ? "left" : "right"}
                                    disabled={!canEditSide}
                                    onChange={(value) => updateReading(index, {is_left: value === "left"})}
                                    transitionDuration={500}
                                />
                            </Box>

                            <ZoneAwareDateTimePicker
                                timezone={timezone}
                                label="Reading Time"
                                value={reading.captured_at ? dayjs(reading.captured_at) : undefined}
                                onChange={(value) => updateReading(index, {captured_at: value.toISOString()})}
                                readonly={!canEditTimes || isReadOnly}
                                dateTimeProps={{
                                    "data-testid": `plr-captured-at-${index}`,
                                    required: false,
                                }}
                            />

                            <NaiveDateTimePicker
                                label="Device Time"
                                description="This does not have a time zone, and should match the PLR device datetime exactly, irrespective of current Neurotune timezone"
                                value={parseNaiveDeviceTime(reading.device_time)}
                                onChange={(value) => updateReading(index, {device_time: value.format(DEVICE_TIME_FORMAT)})}
                                readonly={!canEditTimes || isReadOnly}
                                dateTimeProps={{
                                    "data-testid": `plr-device-time-${index}`,
                                    required: false,
                                }}
                            />
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};
